import React, { useEffect, useState } from "react";
import axios from "./axiosConfig";
import ChevronToggleButton from "./ChevronToggleButton";
import "./Admin.css";

const COMPETITION_LABELS = { 39: "Premier League", 1: "World Cup", 2: "Champions League" };
const FORMAT_LABELS = {
  score_prediction: "Score Predictor",
  last_one_standing: "Last One Standing",
};

export default function Admin({ onClose }) {
  const [users, setUsers] = useState(null);
  const [leagues, setLeagues] = useState(null);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLeaguesExpanded, setIsLeaguesExpanded] = useState(true);
  const [isUsersExpanded, setIsUsersExpanded] = useState(true);
  const [copiedCode, setCopiedCode] = useState("");
  const [openLeagueId, setOpenLeagueId] = useState("");
  const [leagueMembers, setLeagueMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [deleteBusyLeagueId, setDeleteBusyLeagueId] = useState("");
  const [removeBusyKey, setRemoveBusyKey] = useState("");
  const [userBusyKey, setUserBusyKey] = useState("");
  const [emailBusyKey, setEmailBusyKey] = useState("");
  const [bulkEmailBusy, setBulkEmailBusy] = useState("");
  const [dbHealth, setDbHealth] = useState(null);
  const [isDbHealthExpanded, setIsDbHealthExpanded] = useState(true);
  const [membershipBusyKey, setMembershipBusyKey] = useState("");

  const loadDashboardData = () => {
    setError("");
    Promise.all([
      axios.get("/admin/users"),
      axios.get("/admin/leagues"),
      axios.get("/admin/db-health"),
    ])
      .then(([usersRes, leaguesRes, healthRes]) => {
        setUsers(usersRes.data.users || []);
        setLeagues(leaguesRes.data.leagues || []);
        setDbHealth(healthRes.data);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Failed to load admin data.");
      });
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleToggleMembership = async (uid, leagueId, currentlyIn) => {
    const busyKey = `${uid}:${leagueId}`;
    setMembershipBusyKey(busyKey);
    setError("");
    try {
      if (currentlyIn) {
        await axios.delete(`/admin/global-memberships/${encodeURIComponent(uid)}/${encodeURIComponent(leagueId)}`);
      } else {
        await axios.post(`/admin/global-memberships/${encodeURIComponent(uid)}/${encodeURIComponent(leagueId)}`);
      }
      loadDashboardData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update membership.");
    } finally {
      setMembershipBusyKey("");
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(""), 2000);
    });
  };

  const privateLeagues =
    leagues?.filter((l) => l.visibility === "private") ?? [];
  const allLeagues = leagues ?? [];
  const allUsers = users ?? [];
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredLeagues = !normalizedSearch
    ? allLeagues
    : allLeagues.filter((league) => {
        const haystack = [
          league.id,
          league.name,
          league.ownerUsername,
          league.ownerDisplayName,
          league.ownerEmail,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(normalizedSearch);
      });
  const filteredUsers = !normalizedSearch
    ? allUsers
    : allUsers.filter((user) => {
        const haystack = [user.uid, user.displayName, user.email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(normalizedSearch);
      });
  const selectedLeague = allLeagues.find(
    (league) => league.id === openLeagueId,
  );

  const handleToggleMembers = async (league) => {
    setActionMessage("");
    if (openLeagueId === league.id) {
      setOpenLeagueId("");
      setLeagueMembers([]);
      return;
    }

    setMembersLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `/admin/leagues/${encodeURIComponent(league.id)}/members`,
      );
      setOpenLeagueId(league.id);
      setLeagueMembers(res.data.members || []);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to load league members.",
      );
    } finally {
      setMembersLoading(false);
    }
  };

  const handleDeleteLeague = async (league) => {
    const confirmed = window.confirm(
      `Delete league "${league.name}"? This removes league memberships and predictions.`,
    );
    if (!confirmed) return;

    setDeleteBusyLeagueId(league.id);
    setError("");
    setActionMessage("");
    try {
      await axios.delete(`/admin/leagues/${encodeURIComponent(league.id)}`);
      if (openLeagueId === league.id) {
        setOpenLeagueId("");
        setLeagueMembers([]);
      }
      setActionMessage(`League ${league.name} deleted.`);
      loadDashboardData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete league.");
    } finally {
      setDeleteBusyLeagueId("");
    }
  };

  const handleRemoveMember = async (league, member) => {
    const confirmed = window.confirm(
      `Remove ${member.displayName || member.username} from ${league.name}?`,
    );
    if (!confirmed) return;

    const busyKey = `${league.id}:${member.username}`;
    setRemoveBusyKey(busyKey);
    setError("");
    setActionMessage("");
    try {
      await axios.delete(
        `/admin/leagues/${encodeURIComponent(league.id)}/members/${encodeURIComponent(member.username)}`,
      );
      const refreshed = await axios.get(
        `/admin/leagues/${encodeURIComponent(league.id)}/members`,
      );
      setLeagueMembers(refreshed.data.members || []);
      setActionMessage(
        `Removed ${member.displayName || member.username} from ${league.name}.`,
      );
      loadDashboardData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to remove member.");
    } finally {
      setRemoveBusyKey("");
    }
  };

  const handleSetUserDisabled = async (user, disabled) => {
    const action = disabled ? "disable" : "enable";
    const confirmed = window.confirm(
      `${disabled ? "Disable" : "Enable"} ${user.displayName || user.uid}?`,
    );
    if (!confirmed) return;

    setUserBusyKey(`${user.uid}:${action}`);
    setError("");
    setActionMessage("");
    try {
      await axios.patch(
        `/admin/users/${encodeURIComponent(user.uid)}/${action}`,
      );
      setActionMessage(
        `${user.displayName || user.uid} ${disabled ? "disabled" : "enabled"}.`,
      );
      loadDashboardData();
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to ${action} user.`);
    } finally {
      setUserBusyKey("");
    }
  };

  const handleToggleEmail = async (user) => {
    const action = user.emailPaused ? "resume-emails" : "pause-emails";
    const busyKey = `${user.uid}:${action}`;
    setEmailBusyKey(busyKey);
    setError("");
    setActionMessage("");
    try {
      await axios.post(
        `/admin/users/${encodeURIComponent(user.uid)}/${action}`,
        { email: user.email },
      );
      setActionMessage(
        `Emails ${user.emailPaused ? "resumed" : "paused"} for ${user.displayName || user.uid}.`,
      );
      loadDashboardData();
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to ${action.replace("-", " ")}.`);
    } finally {
      setEmailBusyKey("");
    }
  };

  const handleBulkEmail = async (action) => {
    const label = action === "pause-all" ? "pause emails for all users except yourself" : "resume emails for all users";
    const confirmed = window.confirm(`This will ${label}. Continue?`);
    if (!confirmed) return;

    setBulkEmailBusy(action);
    setError("");
    setActionMessage("");
    try {
      const res = await axios.post(`/admin/emails/${action}`);
      setActionMessage(res.data.message);
      loadDashboardData();
    } catch (err) {
      setError(err?.response?.data?.message || "Bulk email action failed.");
    } finally {
      setBulkEmailBusy("");
    }
  };

  const handleDeleteUser = async (user) => {
    const confirmed = window.confirm(
      `Permanently delete ${user.displayName || user.uid}? This cannot be undone.`,
    );
    if (!confirmed) return;

    setUserBusyKey(`${user.uid}:delete`);
    setError("");
    setActionMessage("");
    try {
      await axios.delete(`/admin/users/${encodeURIComponent(user.uid)}`);
      setActionMessage(`${user.displayName || user.uid} deleted.`);
      loadDashboardData();
    } catch (err) {
      const ownedLeagues = err?.response?.data?.ownedLeagues || [];
      if (ownedLeagues.length > 0) {
        const leagueList = ownedLeagues
          .map((league) => `• ${league.name}`)
          .join("\n");
        const message = `Cannot delete this user.\n\nThey still own the following league(s):\n\n${leagueList}\n\nTransfer or delete these leagues first.`;
        alert(message);
        setError(
          `Cannot delete user: they own ${ownedLeagues.length} league(s).`,
        );
      } else {
        setError(err?.response?.data?.message || "Failed to delete user.");
      }
    } finally {
      setUserBusyKey("");
    }
  };

  return (
    <section className="admin-panel">
      <div className="admin-header">
        <h2 className="admin-title">Admin Dashboard</h2>
        <button className="admin-close-btn league-action-tab" onClick={onClose}>
          Close
        </button>
      </div>

      {error && <p className="status-message lock-message">{error}</p>}
      {actionMessage && <p className="admin-action-message">{actionMessage}</p>}

      {/* Stats bar */}
      {users && leagues && (
        <div className="admin-stats-bar">
          <div className="admin-stat">
            <span className="admin-stat-value">{users.length}</span>
            <span className="admin-stat-label">Users</span>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-value">{leagues.length}</span>
            <span className="admin-stat-label">Leagues</span>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-value">{privateLeagues.length}</span>
            <span className="admin-stat-label">Private Leagues</span>
          </div>
        </div>
      )}

      <div className="admin-search-row">
        <label className="admin-search-label" htmlFor="admin-search-input">
          Search
        </label>
        <input
          id="admin-search-input"
          className="admin-search-input"
          type="text"
          placeholder="League name, UID/username, or email"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      {/* Leagues table */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h3 className="admin-section-title">
            Leagues ({filteredLeagues.length})
          </h3>
          <ChevronToggleButton
            isExpanded={isLeaguesExpanded}
            onClick={() => {
              setIsLeaguesExpanded((prev) => !prev);
              if (isLeaguesExpanded) {
                setOpenLeagueId("");
                setLeagueMembers([]);
              }
            }}
            expandedLabel="Collapse leagues"
            collapsedLabel="Expand leagues"
          />
        </div>
        {isLeaguesExpanded && (
          <p className="admin-section-note">
            Public leagues do not use join codes and can be joined via league
            ID.
          </p>
        )}
        {isLeaguesExpanded && !leagues ? (
          <p className="helper-text">Loading…</p>
        ) : isLeaguesExpanded ? (
          <div className="admin-table-wrap admin-table-wrap-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>League ID</th>
                  <th>Name</th>
                  <th>Competition</th>
                  <th>Format</th>
                  <th>Type</th>
                  <th>Members</th>
                  <th>Owner</th>
                  <th>Owner Email</th>
                  <th>Created</th>
                  <th>Join Code</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeagues.map((league) => (
                  <tr key={league.id}>
                    <td className="admin-cell-muted">{league.id}</td>
                    <td>{league.name}</td>
                    <td>
                      {COMPETITION_LABELS[league.competitionId] ??
                        league.competitionId}
                    </td>
                    <td>
                      {FORMAT_LABELS[league.gameFormat] ?? league.gameFormat}
                    </td>
                    <td>
                      <span
                        className={`admin-badge admin-badge-${league.visibility}`}
                      >
                        {league.visibility}
                      </span>
                    </td>
                    <td>{league.memberCount ?? "—"}</td>
                    <td className="admin-cell-muted">
                      {league.ownerDisplayName || league.ownerUsername}
                    </td>
                    <td className="admin-cell-muted">
                      {league.ownerEmail || "—"}
                    </td>
                    <td className="admin-cell-muted">
                      {league.createdAt
                        ? new Date(league.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      {league.joinCode ? (
                        <span className="admin-code-cell">
                          <code className="admin-code">{league.joinCode}</code>
                          <button
                            className="admin-copy-btn"
                            onClick={() => handleCopyCode(league.joinCode)}
                            title="Copy join code"
                          >
                            {copiedCode === league.joinCode ? "✓" : "Copy"}
                          </button>
                        </span>
                      ) : (
                        <span className="admin-cell-muted">
                          {league.visibility === "public" ? "Open" : "—"}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="admin-row-actions">
                        <ChevronToggleButton
                          isExpanded={openLeagueId === league.id}
                          onClick={() => handleToggleMembers(league)}
                          expandedLabel={`Collapse members for ${league.name}`}
                          collapsedLabel={`Expand members for ${league.name}`}
                          expandedTitle="Collapse members"
                          collapsedTitle="Expand members"
                        />
                        <button
                          className="admin-inline-btn admin-inline-btn-danger"
                          onClick={() => handleDeleteLeague(league)}
                          disabled={
                            deleteBusyLeagueId === league.id ||
                            league.ownerUsername === "GLOBAL_LEAGUE_SYSTEM"
                          }
                          title={
                            league.ownerUsername === "GLOBAL_LEAGUE_SYSTEM"
                              ? "System leagues cannot be deleted"
                              : "Delete league"
                          }
                        >
                          {deleteBusyLeagueId === league.id
                            ? "Deleting..."
                            : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {isLeaguesExpanded && openLeagueId && selectedLeague && (
          <div className="admin-members-panel">
            <h4 className="admin-members-title">
              Members: {selectedLeague.name}
            </h4>
            {membersLoading ? (
              <p className="helper-text">Loading members...</p>
            ) : leagueMembers.length === 0 ? (
              <p className="helper-text">No members found.</p>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table admin-members-table">
                  <thead>
                    <tr>
                      <th>Display Name</th>
                      <th>UID</th>
                      <th>Email</th>
                      <th>Joined</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leagueMembers.map((member) => {
                      const isOwner =
                        member.username === selectedLeague.ownerUsername;
                      const busyKey = `${selectedLeague.id}:${member.username}`;
                      return (
                        <tr key={`${selectedLeague.id}-${member.username}`}>
                          <td>{member.displayName || member.username}</td>
                          <td className="admin-cell-muted">
                            {member.username}
                          </td>
                          <td className="admin-cell-muted">
                            {member.email || "—"}
                          </td>
                          <td className="admin-cell-muted">
                            {member.joinedAt
                              ? new Date(member.joinedAt).toLocaleDateString()
                              : "—"}
                          </td>
                          <td>
                            <button
                              className="admin-inline-btn admin-inline-btn-danger"
                              disabled={isOwner || removeBusyKey === busyKey}
                              onClick={() =>
                                handleRemoveMember(selectedLeague, member)
                              }
                              title={
                                isOwner
                                  ? "League owner cannot be removed"
                                  : "Remove user"
                              }
                            >
                              {removeBusyKey === busyKey
                                ? "Removing..."
                                : "Remove"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Users table */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h3 className="admin-section-title">
            Users ({filteredUsers.length})
          </h3>
          <div className="admin-row-actions">
            <button
              className="admin-inline-btn admin-inline-btn-danger"
              onClick={() => handleBulkEmail("pause-all")}
              disabled={!!bulkEmailBusy}
              title="Pause emails for all users except yourself"
            >
              {bulkEmailBusy === "pause-all" ? "Working..." : "Pause all emails"}
            </button>
            <button
              className="admin-inline-btn"
              onClick={() => handleBulkEmail("resume-all")}
              disabled={!!bulkEmailBusy}
              title="Resume emails for all users"
            >
              {bulkEmailBusy === "resume-all" ? "Working..." : "Resume all emails"}
            </button>
            <ChevronToggleButton
              isExpanded={isUsersExpanded}
              onClick={() => setIsUsersExpanded((prev) => !prev)}
              expandedLabel="Collapse users"
              collapsedLabel="Expand users"
            />
          </div>
        </div>
        {isUsersExpanded && !users ? (
          <p className="helper-text">Loading…</p>
        ) : isUsersExpanded ? (
          <div className="admin-table-wrap admin-table-wrap-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th>Email</th>
                  <th>Account</th>
                  <th>Notifications</th>
                  <th>Last Active</th>
                  <th>Emails</th>
                  <th>Enable / Disable</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.uid}>
                    <td>
                      {user.displayName || (
                        <span className="admin-cell-muted">—</span>
                      )}
                    </td>
                    <td>
                      {user.email || (
                        <span className="admin-cell-muted">—</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`admin-badge ${
                          user.disabled
                            ? "admin-badge-disabled"
                            : "admin-badge-enabled"
                        }`}
                      >
                        {user.disabled ? "Disabled" : "Active"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`admin-badge ${
                          !user.emailPaused
                            ? "admin-badge-enabled"
                            : user.emailPauseReason === "admin"
                            ? "admin-badge-warning"
                            : "admin-badge-disabled"
                        }`}
                      >
                        {!user.emailPaused
                          ? "Subscribed"
                          : user.emailPauseReason === "admin"
                          ? "Paused"
                          : "Unsubscribed"}
                      </span>
                    </td>
                    <td className="admin-cell-muted">
                      {user.updatedAt
                        ? new Date(user.updatedAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      {!user.emailPaused ? (
                        <button
                          className="admin-inline-btn admin-inline-btn-danger"
                          disabled={emailBusyKey.startsWith(`${user.uid}:`)}
                          onClick={() => handleToggleEmail(user)}
                        >
                          {emailBusyKey === `${user.uid}:pause-emails` ? "Working..." : "Pause"}
                        </button>
                      ) : user.emailPauseReason === "admin" ? (
                        <button
                          className="admin-inline-btn"
                          disabled={emailBusyKey.startsWith(`${user.uid}:`)}
                          onClick={() => handleToggleEmail(user)}
                        >
                          {emailBusyKey === `${user.uid}:resume-emails` ? "Working..." : "Resume"}
                        </button>
                      ) : (
                        <button
                          className="admin-inline-btn"
                          disabled={emailBusyKey.startsWith(`${user.uid}:`)}
                          onClick={() => handleToggleEmail(user)}
                        >
                          {emailBusyKey === `${user.uid}:resume-emails` ? "Working..." : "Re-subscribe"}
                        </button>
                      )}
                    </td>
                    <td>
                      {user.disabled ? (
                        <button
                          className="admin-inline-btn"
                          disabled={userBusyKey.startsWith(`${user.uid}:`)}
                          onClick={() => handleSetUserDisabled(user, false)}
                        >
                          {userBusyKey === `${user.uid}:enable` ? "Working..." : "Enable"}
                        </button>
                      ) : (
                        <button
                          className="admin-inline-btn admin-inline-btn-danger"
                          disabled={userBusyKey.startsWith(`${user.uid}:`)}
                          onClick={() => handleSetUserDisabled(user, true)}
                        >
                          {userBusyKey === `${user.uid}:disable` ? "Working..." : "Disable"}
                        </button>
                      )}
                    </td>
                    <td>
                      <button
                        className="admin-inline-btn admin-inline-btn-danger"
                        disabled={userBusyKey.startsWith(`${user.uid}:`)}
                        onClick={() => handleDeleteUser(user)}
                      >
                        {userBusyKey === `${user.uid}:delete` ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {/* Database Health */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h3 className="admin-section-title">Database Health</h3>
          <ChevronToggleButton
            isExpanded={isDbHealthExpanded}
            onClick={() => setIsDbHealthExpanded((prev) => !prev)}
            expandedLabel="Collapse database health"
            collapsedLabel="Expand database health"
          />
        </div>
        {isDbHealthExpanded && !dbHealth ? (
          <p className="helper-text">Loading…</p>
        ) : isDbHealthExpanded && dbHealth ? (
          <>
            <div className="admin-stats-bar">
              <div className="admin-stat">
                <span className="admin-stat-value">{dbHealth.totalUsers}</span>
                <span className="admin-stat-label">Total Users</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-value">{dbHealth.totalPredictions.toLocaleString()}</span>
                <span className="admin-stat-label">Total Predictions</span>
              </div>
              {dbHealth.globalLeagues.map((league) => (
                <div key={league.leagueId} className="admin-stat">
                  <span className="admin-stat-value">{league.memberCount}</span>
                  <span className="admin-stat-label">{league.name}</span>
                </div>
              ))}
            </div>
            <p className="admin-section-note">
              Users with predictions but missing from a global league are highlighted.
            </p>
            <div className="admin-table-wrap admin-table-wrap-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Display Name</th>
                    <th>UID</th>
                    <th>Predictions</th>
                    <th>PL</th>
                    <th>World Cup</th>
                    <th>Champions League</th>
                  </tr>
                </thead>
                <tbody>
                  {dbHealth.users
                    .sort((a, b) => b.predictionCount - a.predictionCount)
                    .map((user) => {
                      const missing =
                        user.predictionCount > 0 &&
                        (!user.inPl || !user.inWc || !user.inCl);
                      return (
                        <tr
                          key={user.uid}
                          className={missing ? "admin-row-warning" : ""}
                        >
                          <td>{user.displayName || <span className="admin-cell-muted">—</span>}</td>
                          <td className="admin-cell-muted">{user.uid}</td>
                          <td>{user.predictionCount.toLocaleString()}</td>
                          {[
                            { leagueId: "global_39_score", inLeague: user.inPl },
                            { leagueId: "global_1_score",  inLeague: user.inWc },
                            { leagueId: "global_2_score",  inLeague: user.inCl },
                          ].map(({ leagueId, inLeague }) => {
                            const busy = membershipBusyKey === `${user.uid}:${leagueId}`;
                            return (
                              <td key={leagueId}>
                                <button
                                  className={`admin-inline-btn${inLeague ? " admin-inline-btn-danger" : ""}`}
                                  disabled={!!membershipBusyKey}
                                  onClick={() => handleToggleMembership(user.uid, leagueId, inLeague)}
                                  title={inLeague ? "Remove from league" : "Add to league"}
                                >
                                  {busy ? "…" : inLeague ? "Remove" : "Add"}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
